import React from 'react';
import { Group, Rect, Text, Circle, Line, Image } from 'react-konva';
import useImage from 'use-image';

interface CanvasUIElementRendererProps {
    element: UIElement;
    isSelected: boolean;
    onSelect: () => void;
    scale: number;
    screenId: string; // Added for layer actions
    isStacked?: boolean;
    stackNumber?: number;
    displayProperties: {
        foregroundColor: string;
        buttonColor: string;
        buttonTextColor: string;
        backgroundColor: string;
    };
    screenDimensions?: {
        width: number;
        height: number;
    };
    onRightClick?: (element: UIElement, screenId: string, event: any) => void;
    onElementDragStart?: (
        element: UIElement,
        screenId: string,
        event: any,
    ) => void;
}

// Helper component for rendering images with fallback
function ImageWithFallback({
    imageUrl,
    width,
    height,
    cornerRadius = 0,
    useImageDimensions = false,
    scale = 1,
    fallbackContent,
}: {
    imageUrl?: string;
    width: number;
    height: number;
    cornerRadius?: number;
    useImageDimensions?: boolean;
    scale?: number;
    fallbackContent: React.ReactNode;
}) {
    const [image, status] = useImage(imageUrl || '');

    // Debug logging
    console.log('ImageWithFallback:', {
        imageUrl,
        status,
        hasImage: !!image,
        useImageDimensions,
    });

    if (image && imageUrl && status === 'loaded') {
        let renderWidth: number;
        let renderHeight: number;

        if (useImageDimensions) {
            // Use actual image dimensions, scaled appropriately
            renderWidth = image.width;
            renderHeight = image.height;
        } else {
            // Maintain aspect ratio while fitting within the specified width and height
            // The width and height passed in are already scaled by the parent component
            const imageAspectRatio = image.width / image.height;
            const containerAspectRatio = width / height;

            if (imageAspectRatio > containerAspectRatio) {
                // Image is wider than container - fit to width
                renderWidth = width;
                renderHeight = width / imageAspectRatio;
            } else {
                // Image is taller than container - fit to height
                renderWidth = height * imageAspectRatio;
                renderHeight = height;
            }
        }

        return (
            <Image
                image={image}
                width={renderWidth}
                height={renderHeight}
                cornerRadius={cornerRadius}
            />
        );
    }

    return <>{fallbackContent}</>;
}

export default function CanvasUIElementRenderer({
    element,
    isSelected,
    onSelect,
    scale,
    screenId,
    isStacked = false,
    stackNumber = 1,
    displayProperties,
    screenDimensions,
    onRightClick,
    onElementDragStart,
}: CanvasUIElementRendererProps) {
    const metadata = element.metadata as any;

    // Canvas scale constant for consistent sizing
    const CANVAS_SCALE = 0.25;

    // Base dimensions (scaled down for canvas preview)
    const baseWidth = Math.max(20, (metadata.width || 100) * scale);
    const baseHeight = Math.max(15, (metadata.height || 30) * scale);

    // Helper function for device-aware scaling of UI elements (borders, radii, etc.)
    // This scales small UI elements like borders and radii appropriately for the device
    const scaleUIElement = (value: number) => {
        // Use a smaller scaling factor for UI elements to keep them visible but proportional
        return Math.max(0.5, value * scale * 0.5);
    };

    // Elements are now positioned at their anchor point by ScreenCanvasNode
    // We just need to center them around that point
    const getCenteredPosition = (width: number, height: number) => {
        return {
            x: -width / 2, // Center horizontally around anchor
            y: -height / 2, // Center vertically around anchor
        };
    };

    const handleClick = (e: any) => {
        e.cancelBubble = true;
        onSelect();
    };

    const handleRightClick = (e: any) => {
        // Stop event propagation immediately
        e.cancelBubble = true;

        // In Konva, the actual DOM event is in e.evt
        const domEvent = e.evt;
        if (domEvent) {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            domEvent.stopImmediatePropagation();
        }

        // Also try to stop Konva event propagation
        if (e.target) {
            e.target.stopDrag?.();
        }

        // Show context menu if handler is provided
        if (onRightClick) {
            onRightClick(element, screenId, e);
        }
    };

    // Use the existing handleRightClick for all context menu events
    const konvaContextMenuHandler = handleRightClick;

    const handleMouseDown = (e: any) => {
        // Only handle left mouse button for dragging
        const domEvent = e.evt;
        if (domEvent && domEvent.button === 0 && onElementDragStart) {
            // Stop event propagation to prevent canvas dragging
            e.cancelBubble = true;
            domEvent.preventDefault();
            domEvent.stopPropagation();
            domEvent.stopImmediatePropagation();

            // Disable Konva's built-in dragging
            e.target.stopDrag?.();

            // Start drag for offset adjustment
            onElementDragStart(element, screenId, e);
        }
    };

    // Render stacking indicator for all stacked elements
    const renderStackIndicator = (width: number, height: number) => {
        if (!isStacked) return null;

        return (
            <Circle
                x={width - scaleUIElement(6)}
                y={scaleUIElement(6)}
                radius={scaleUIElement(4)}
                fill="#10B981"
                stroke="#FFFFFF"
                strokeWidth={scaleUIElement(1)}
            />
        );
    };

    // Common selection outline with stacking indicator
    const renderSelectionOutline = (width: number, height: number) => {
        if (!isSelected) return null;

        const elements = [
            <Rect
                key="outline"
                x={-2}
                y={-2}
                width={width + 4}
                height={height + 4}
                stroke="#3B82F6"
                strokeWidth={3}
                dash={[6, 6]}
                cornerRadius={scaleUIElement(4)}
            />,
        ];

        // Add stack number indicator if element is stacked
        if (isStacked && stackNumber > 1) {
            elements.push(
                <Circle
                    key="stack-indicator"
                    x={width + scaleUIElement(2)}
                    y={-scaleUIElement(8)}
                    radius={scaleUIElement(6)}
                    fill="#3B82F6"
                />,
                <Text
                    key="stack-number"
                    x={width + scaleUIElement(2)}
                    y={-scaleUIElement(8)}
                    text={stackNumber.toString()}
                    fontSize={8}
                    fontFamily="Arial"
                    fill="white"
                    width={12}
                    align="center"
                    verticalAlign="middle"
                    fontStyle="bold"
                />,
            );
        }

        return elements;
    };

    switch (element.type) {
        case 'text':
            const textContent = metadata.text || 'Text Element';
            const fontSize = Math.max(
                8,
                (metadata.fontSize || 16) * scale * 0.5,
            );
            const textWidth = Math.max(
                baseWidth,
                textContent.length * fontSize * 0.6,
            );
            const textHeight = fontSize + 4;
            const textPos = getCenteredPosition(textWidth, textHeight);

            return (
                <Group
                    x={textPos.x}
                    y={textPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(textWidth, textHeight)}
                    {renderStackIndicator(textWidth, textHeight)}
                    <Text
                        text={textContent}
                        fontSize={fontSize}
                        fontFamily={metadata.fontFace || 'Arial'}
                        fill={
                            metadata.fontColor ||
                            displayProperties.foregroundColor
                        }
                        width={textWidth}
                        fontStyle={
                            metadata.fontWeight >= 600 ? 'bold' : 'normal'
                        }
                        align={
                            metadata.alignment === 'leading'
                                ? 'left'
                                : metadata.alignment === 'trailing'
                                ? 'right'
                                : 'center'
                        }
                    />
                </Group>
            );

        case 'dynamicText':
            const dynamicTextContent = `[${metadata.identifier || 'dynamic'}]`;
            const dynamicFontSize = Math.max(
                8,
                (metadata.fontSize || 16) * scale * 0.5,
            );
            const dynamicTextWidth = Math.max(
                baseWidth,
                dynamicTextContent.length * dynamicFontSize * 0.6,
            );
            const dynamicTextHeight = dynamicFontSize + 4;
            const dynamicTextPos = getCenteredPosition(
                dynamicTextWidth,
                dynamicTextHeight,
            );

            return (
                <Group
                    x={dynamicTextPos.x}
                    y={dynamicTextPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(
                        dynamicTextWidth,
                        dynamicTextHeight,
                    )}
                    {renderStackIndicator(dynamicTextWidth, dynamicTextHeight)}
                    <Text
                        text={dynamicTextContent}
                        fontSize={dynamicFontSize}
                        fontFamily={metadata.fontFace || 'Arial'}
                        fill={
                            metadata.fontColor ||
                            displayProperties.foregroundColor
                        }
                        width={dynamicTextWidth}
                        fontStyle="italic"
                        align={
                            metadata.alignment === 'leading'
                                ? 'left'
                                : metadata.alignment === 'trailing'
                                ? 'right'
                                : 'center'
                        }
                    />
                </Group>
            );

        case 'button':
            const buttonText = metadata.text || 'Button';
            const buttonHeight = baseHeight;
            const buttonFontSize = Math.max(
                10,
                (metadata.fontSize || 16) * scale,
            );

            const buttonWidth = (buttonText.length * buttonFontSize) / 2;
            const buttonPos = getCenteredPosition(buttonWidth, buttonHeight);

            // Apply padding values with proper scaling
            const horizontalPadding = (metadata.horizontalPadding || 0) * scale;
            //const verticalPadding = (metadata.verticalPadding || 0) * scale;

            const fallbackButtonContent = (
                <>
                    <Rect
                        width={buttonWidth}
                        height={buttonHeight}
                        fill={
                            metadata.backgroundColor ||
                            displayProperties.buttonColor
                        }
                        cornerRadius={(metadata.cornerRadius || 4) * scale}
                        stroke={
                            metadata.borderColor ||
                            displayProperties.buttonColor
                        }
                        strokeWidth={
                            metadata.borderSize
                                ? metadata.borderSize * scale
                                : scaleUIElement(1)
                        }
                    />
                    <Text
                        x={horizontalPadding}
                        y={buttonHeight / 2 - buttonFontSize / 2}
                        text={buttonText}
                        fontSize={buttonFontSize}
                        fontFamily={metadata.fontFace || 'Arial'}
                        fill={
                            metadata.fontColor ||
                            displayProperties.buttonTextColor
                        }
                        width={buttonWidth - horizontalPadding * 2}
                        align="center"
                        verticalAlign="middle"
                        fontStyle={
                            metadata.fontWeight >= 600 ? 'bold' : 'normal'
                        }
                        wrap="none"
                    />
                </>
            );

            return (
                <Group
                    x={buttonPos.x}
                    y={buttonPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(buttonWidth, buttonHeight)}
                    {renderStackIndicator(buttonWidth, buttonHeight)}
                    {metadata.imageUrl ? (
                        <ImageWithFallback
                            imageUrl={metadata.imageUrl}
                            width={buttonWidth}
                            height={buttonHeight}
                            cornerRadius={(metadata.cornerRadius || 4) * scale}
                            useImageDimensions={metadata.useImageDimensions}
                            scale={scale}
                            fallbackContent={fallbackButtonContent}
                        />
                    ) : (
                        fallbackButtonContent
                    )}
                </Group>
            );

        case 'image':
            const imageWidth = Math.max(40, baseWidth) * scale;
            const imageHeight = Math.max(30, baseHeight) * scale;
            const imagePos = getCenteredPosition(imageWidth, imageHeight);

            const fallbackImageContent = (
                <>
                    <Rect
                        width={imageWidth}
                        height={imageHeight}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={(metadata.cornerRadius || 0) * scale}
                    />
                    {/* Image placeholder icon */}
                    <Rect
                        x={imageWidth / 2 - 8}
                        y={imageHeight / 2 - 6}
                        width={16}
                        height={12}
                        fill="#10B981"
                        cornerRadius={2}
                    />
                    <Text
                        x={imageWidth / 2}
                        y={imageHeight / 2 + 8}
                        text="IMG"
                        fontSize={8}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={imageWidth}
                        align="center"
                    />
                </>
            );

            return (
                <Group
                    x={imagePos.x}
                    y={imagePos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(imageWidth, imageHeight)}
                    {renderStackIndicator(imageWidth, imageHeight)}
                    <ImageWithFallback
                        imageUrl={metadata.imageUrl}
                        width={imageWidth}
                        height={imageHeight}
                        cornerRadius={(metadata.cornerRadius || 0) * scale}
                        useImageDimensions={metadata.useImageDimensions}
                        scale={scale}
                        fallbackContent={fallbackImageContent}
                    />
                </Group>
            );

        case 'input':
            // Use canvas scale only for input sizing (not device scale)
            const inputWidth = Math.max(
                20,
                (metadata.width || 100) * CANVAS_SCALE,
            );
            const inputHeight = Math.max(
                15,
                (metadata.height || 30) * CANVAS_SCALE,
            );
            const inputPlaceholder =
                metadata.defaultValue ||
                `${metadata.inputType || 'string'} input`;
            const inputPos = getCenteredPosition(inputWidth, inputHeight);

            return (
                <Group
                    x={inputPos.x}
                    y={inputPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(inputWidth, inputHeight)}
                    <Rect
                        width={inputWidth}
                        height={inputHeight}
                        fill="white"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={(metadata.cornerRadius || 4) * scale}
                    />
                    <Text
                        x={4}
                        y={inputHeight / 2}
                        text={inputPlaceholder}
                        fontSize={Math.max(
                            6,
                            (metadata.fontSize || 12) * scale * 0.5,
                        )}
                        fontFamily={metadata.fontFace || 'Arial'}
                        fill={
                            metadata.fontColor ||
                            displayProperties.foregroundColor
                        }
                        width={inputWidth - 8}
                        verticalAlign="middle"
                        opacity={0.7}
                    />
                </Group>
            );

        case 'currentSessionQR':
        case 'paymentQR':
        case 'selectedQR':
            const qrSize = Math.max(30, Math.min(baseWidth, baseHeight));
            const qrPos = getCenteredPosition(qrSize, qrSize);
            const qrModule = qrSize / 25; // QR code module size (25x25 grid)
            const qrForeground =
                metadata.foregroundColor ||
                displayProperties.foregroundColor ||
                '#000000';
            const qrBackground =
                metadata.backgroundColor ||
                displayProperties.backgroundColor ||
                '#FFFFFF';

            // Determine QR type label and color
            let qrTypeLabel = '';
            let qrTypeBg = '#3B82F6';
            if (element.type === 'paymentQR') {
                qrTypeLabel = '$';
                qrTypeBg = '#10B981';
            } else if (element.type === 'selectedQR') {
                qrTypeLabel = '✓';
                qrTypeBg = '#8B5CF6';
            } else {
                qrTypeLabel = '⚡';
                qrTypeBg = '#3B82F6';
            }

            return (
                <Group
                    x={qrPos.x}
                    y={qrPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(qrSize, qrSize)}
                    {/* QR Code background */}
                    <Rect
                        width={qrSize}
                        height={qrSize}
                        fill={qrBackground}
                        stroke="#E5E7EB"
                        strokeWidth={scaleUIElement(1)}
                    />

                    {/* Realistic QR pattern with position markers */}
                    {/* Generate QR-like pattern */}
                    {Array.from({ length: 25 }).map((_, i) =>
                        Array.from({ length: 25 }).map((_, j) => {
                            // Top-left position marker (7x7)
                            if (i < 7 && j < 7) {
                                const isOuter =
                                    i === 0 || i === 6 || j === 0 || j === 6;
                                const isInner =
                                    i >= 2 && i <= 4 && j >= 2 && j <= 4;
                                if (isOuter || isInner) {
                                    return (
                                        <Rect
                                            key={`${i}-${j}`}
                                            x={i * qrModule}
                                            y={j * qrModule}
                                            width={qrModule}
                                            height={qrModule}
                                            fill={qrForeground}
                                        />
                                    );
                                }
                            }

                            // Top-right position marker (7x7)
                            if (i >= 18 && j < 7) {
                                const localI = i - 18;
                                const isOuter =
                                    localI === 0 ||
                                    localI === 6 ||
                                    j === 0 ||
                                    j === 6;
                                const isInner =
                                    localI >= 2 &&
                                    localI <= 4 &&
                                    j >= 2 &&
                                    j <= 4;
                                if (isOuter || isInner) {
                                    return (
                                        <Rect
                                            key={`${i}-${j}`}
                                            x={i * qrModule}
                                            y={j * qrModule}
                                            width={qrModule}
                                            height={qrModule}
                                            fill={qrForeground}
                                        />
                                    );
                                }
                            }

                            // Bottom-left position marker (7x7)
                            if (i < 7 && j >= 18) {
                                const localJ = j - 18;
                                const isOuter =
                                    i === 0 ||
                                    i === 6 ||
                                    localJ === 0 ||
                                    localJ === 6;
                                const isInner =
                                    i >= 2 &&
                                    i <= 4 &&
                                    localJ >= 2 &&
                                    localJ <= 4;
                                if (isOuter || isInner) {
                                    return (
                                        <Rect
                                            key={`${i}-${j}`}
                                            x={i * qrModule}
                                            y={j * qrModule}
                                            width={qrModule}
                                            height={qrModule}
                                            fill={qrForeground}
                                        />
                                    );
                                }
                            }

                            // Random pattern for data area (pseudo-random but deterministic)
                            const seed = (i * 31 + j * 17) % 100;
                            if (
                                seed < 45 &&
                                !(i < 9 && j < 9) &&
                                !(i >= 16 && j < 9) &&
                                !(i < 9 && j >= 16)
                            ) {
                                return (
                                    <Rect
                                        key={`${i}-${j}`}
                                        x={i * qrModule}
                                        y={j * qrModule}
                                        width={qrModule}
                                        height={qrModule}
                                        fill={qrForeground}
                                    />
                                );
                            }
                            return null;
                        }),
                    )}

                    {/* Type indicator badge in top-right corner */}
                    <Rect
                        x={qrSize - scaleUIElement(102)}
                        y={scaleUIElement(2)}
                        width={scaleUIElement(100)}
                        height={scaleUIElement(100)}
                        fill={qrTypeBg}
                        cornerRadius={scaleUIElement(8)}
                    />
                    <Text
                        x={qrSize - scaleUIElement(52)}
                        y={scaleUIElement(52)}
                        text={qrTypeLabel}
                        fontSize={scaleUIElement(56)}
                        fontFamily="Arial"
                        fill="#FFFFFF"
                        align="center"
                        verticalAlign="middle"
                        fontStyle="bold"
                        offsetX={0}
                        offsetY={scaleUIElement(28)}
                    />
                </Group>
            );

        case 'liveView':
            // Use canvas scale only for live view sizing (not device scale)
            const liveWidth = Math.max(
                20,
                (metadata.width || 100) * CANVAS_SCALE,
            );
            const liveHeight = Math.max(
                15,
                (metadata.height || 75) * CANVAS_SCALE,
            );
            const livePos = getCenteredPosition(liveWidth, liveHeight);

            return (
                <Group
                    x={livePos.x}
                    y={livePos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(liveWidth, liveHeight)}
                    <Rect
                        width={liveWidth}
                        height={liveHeight}
                        fill="#000000"
                        cornerRadius={(metadata.cornerRadius || 0) * scale}
                    />
                    {/* Camera icon simulation */}
                    <Circle
                        x={liveWidth / 2}
                        y={liveHeight / 2}
                        radius={Math.min(liveWidth, liveHeight) / 6}
                        fill="#FFFFFF"
                        opacity={0.8}
                    />
                    <Circle
                        x={liveWidth / 2}
                        y={liveHeight / 2}
                        radius={Math.min(liveWidth, liveHeight) / 10}
                        fill="#000000"
                    />
                    <Text
                        x={liveWidth / 2}
                        y={liveHeight / 2}
                        text="LIVE"
                        fontSize={8}
                        fontFamily="Arial"
                        fill="white"
                        width={liveWidth}
                        align="center"
                        verticalAlign="middle"
                        offsetX={liveWidth / 2}
                        offsetY={4}
                        fontStyle="bold"
                    />
                </Group>
            );

        case 'galleryGrid':
        case 'mediaGrid':
        case 'sessionGrid':
            const gridWidth = Math.max(60, baseWidth);
            const gridHeight = Math.max(45, baseHeight);

            // Get columns and spacing from metadata, with defaults
            const columns = (metadata as any).columns || 3;
            // Use scaleUIElement for spacing to prevent excessive scaling
            const spacing = scaleUIElement((metadata as any).spacing || 20);
            const padding = scaleUIElement(8); // Internal padding for the grid container

            // Calculate cell dimensions based on columns and spacing
            const availableWidth =
                gridWidth - padding * 2 - spacing * (columns - 1);
            const cellWidth = Math.max(
                scaleUIElement(4),
                availableWidth / columns,
            );

            // Calculate how many rows we need (show 2 rows for preview)
            const rows = Math.ceil(6 / columns);
            const availableHeight =
                gridHeight - padding * 2 - spacing * (rows - 1);
            const cellHeight = Math.max(
                scaleUIElement(4),
                Math.min(cellWidth, availableHeight / rows),
            ); // Keep cells roughly square

            const gridPos = getCenteredPosition(gridWidth, gridHeight);

            return (
                <Group
                    x={gridPos.x}
                    y={gridPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(gridWidth, gridHeight)}
                    <Rect
                        width={gridWidth}
                        height={gridHeight}
                        fill="#F9FAFB"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={scaleUIElement(4)}
                    />
                    {/* Grid of photo placeholders using dynamic columns and spacing */}
                    {Array.from({ length: Math.min(columns * rows, 6) }).map(
                        (_, i) => {
                            const row = Math.floor(i / columns);
                            const col = i % columns;
                            const x = padding + col * (cellWidth + spacing);
                            const y = padding + row * (cellHeight + spacing);

                            return (
                                <Rect
                                    key={i}
                                    x={x}
                                    y={y}
                                    width={cellWidth}
                                    height={cellHeight}
                                    fill="#E5E7EB"
                                    stroke="#D1D5DB"
                                    strokeWidth={scaleUIElement(1)}
                                    cornerRadius={scaleUIElement(2)}
                                />
                            );
                        },
                    )}
                    <Text
                        x={gridWidth / 2}
                        y={gridHeight - scaleUIElement(12)}
                        text={`${columns} COLS`}
                        fontSize={Math.max(4, scaleUIElement(8))}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={gridWidth}
                        align="center"
                        offsetX={gridWidth / 2}
                    />
                </Group>
            );

        case 'outputPreview':
            const previewWidth = Math.max(
                50,
                (metadata.width || 200) * CANVAS_SCALE,
            );
            const previewHeight = Math.max(
                40,
                (metadata.height || 150) * CANVAS_SCALE,
            );
            const previewPos = getCenteredPosition(previewWidth, previewHeight);

            return (
                <Group
                    x={previewPos.x}
                    y={previewPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(previewWidth, previewHeight)}
                    <Rect
                        width={previewWidth}
                        height={previewHeight}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(2)}
                        strokeDasharray={[scaleUIElement(4), scaleUIElement(4)]}
                        cornerRadius={scaleUIElement(4)}
                    />
                    <Text
                        x={previewWidth / 2}
                        y={previewHeight / 2}
                        text="PREVIEW"
                        fontSize={8}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={previewWidth}
                        align="center"
                        verticalAlign="middle"
                        fontStyle="bold"
                    />
                </Group>
            );

        case 'group':
            const groupWidth = Math.max(80, baseWidth || 120);
            const groupHeight = Math.max(60, baseHeight || 80);
            // Groups are positioned by ScreenCanvasNode, so no additional offset needed
            const groupPos = { x: 0, y: 0 };

            return (
                <Group
                    x={groupPos.x}
                    y={groupPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(groupWidth, groupHeight)}
                    <Rect
                        width={groupWidth}
                        height={groupHeight}
                        fill="transparent"
                        stroke="#8B5CF6"
                        strokeWidth={scaleUIElement(2)}
                        strokeDasharray={[scaleUIElement(6), scaleUIElement(3)]}
                        cornerRadius={scaleUIElement(8)}
                    />
                    <Text
                        x={4}
                        y={4}
                        text={metadata.name || 'Group'}
                        fontSize={8}
                        fontFamily="Arial"
                        fill="#8B5CF6"
                        fontStyle="bold"
                    />
                    {/* Direction indicator */}
                    {metadata.direction === 'row' ? (
                        <Line
                            points={[
                                scaleUIElement(8),
                                groupHeight - scaleUIElement(8),
                                groupWidth - scaleUIElement(8),
                                groupHeight - scaleUIElement(8),
                            ]}
                            stroke="#8B5CF6"
                            strokeWidth={scaleUIElement(2)}
                        />
                    ) : (
                        <Line
                            points={[
                                scaleUIElement(8),
                                scaleUIElement(8),
                                scaleUIElement(8),
                                groupHeight - scaleUIElement(8),
                            ]}
                            stroke="#8B5CF6"
                            strokeWidth={scaleUIElement(2)}
                        />
                    )}
                </Group>
            );

        case 'artworkSelector':
            // For UI element sizing, we should only use canvas scale, not device scale
            // The device scale is for rendering quality, not element sizing
            const artworkWidth = Math.max(
                40,
                (metadata.width || 100) * CANVAS_SCALE,
            );
            const artworkHeight = Math.max(
                30,
                (metadata.height || 100) * CANVAS_SCALE,
            );

            // Calculate total component size (main preview + space for options below)
            const optionAreaHeight = scaleUIElement(15); // Space for options below the main preview
            const totalSelectorWidth = artworkWidth;
            const totalSelectorHeight = artworkHeight + optionAreaHeight;

            const selectorPos = getCenteredPosition(
                totalSelectorWidth,
                totalSelectorHeight,
            );

            // Calculate option dimensions using canvas scale only (not device scale)
            const optionWidth = Math.max(
                4,
                (metadata.optionWidth || 100) * CANVAS_SCALE,
            );
            const optionHeight = Math.max(
                4,
                (metadata.optionHeight || 100) * CANVAS_SCALE,
            );

            return (
                <Group
                    x={selectorPos.x}
                    y={selectorPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(
                        totalSelectorWidth,
                        totalSelectorHeight,
                    )}
                    {/* Main preview area - uses the specified width/height */}
                    <Rect
                        width={artworkWidth}
                        height={artworkHeight}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={scaleUIElement(4)}
                    />
                    {/* Device-aware option thumbnails based on flowIds */}
                    {metadata.flowIds &&
                        Array.from({ length: metadata.flowIds.length }).map(
                            (_, i) => {
                                // Center the options below the main preview
                                const optionSpacing = scaleUIElement(2);
                                const totalOptionsWidth =
                                    metadata.flowIds.length * optionWidth +
                                    (metadata.flowIds.length - 1) *
                                        optionSpacing;
                                const startX =
                                    (artworkWidth - totalOptionsWidth) / 2;
                                const optionX =
                                    startX + i * (optionWidth + optionSpacing);
                                const optionY =
                                    artworkHeight + scaleUIElement(3);

                                // Use metadata colors and border width
                                const borderColor =
                                    metadata.optionBorderColor || '#D1D5DB';
                                const borderWidth =
                                    metadata.optionBorderWidth || 0;

                                return metadata.optionShape === 'circle' ? (
                                    <Circle
                                        key={i}
                                        x={optionX + optionWidth / 2}
                                        y={optionY + optionHeight / 2}
                                        radius={
                                            Math.min(
                                                optionWidth,
                                                optionHeight,
                                            ) / 2
                                        }
                                        fill="#E5E7EB"
                                        stroke={borderColor}
                                        strokeWidth={borderWidth}
                                    />
                                ) : (
                                    <Rect
                                        key={i}
                                        x={optionX}
                                        y={optionY}
                                        width={optionWidth}
                                        height={optionHeight}
                                        fill="#E5E7EB"
                                        stroke={borderColor}
                                        strokeWidth={borderWidth}
                                        cornerRadius={
                                            metadata.optionShape ===
                                            'roundedRectangle'
                                                ? scaleUIElement(2)
                                                : 0
                                        }
                                    />
                                );
                            },
                        )}
                    <Text
                        x={artworkWidth / 2}
                        y={artworkHeight / 2}
                        text="ARTWORK"
                        fontSize={6}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={artworkWidth}
                        align="center"
                        verticalAlign="middle"
                        offsetX={artworkWidth / 2}
                        offsetY={3}
                    />
                    {/* Show actual main preview dimensions */}
                    <Text
                        x={artworkWidth / 2}
                        y={totalSelectorHeight - 2}
                        text={`${metadata.width || 100}×${
                            metadata.height || 100
                        }pt`}
                        fontSize={4}
                        fontFamily="Arial"
                        fill="#9CA3AF"
                        width={artworkWidth}
                        align="center"
                    />
                </Group>
            );

        case 'codeScanner':
            const scannerWidth = Math.max(60, baseWidth);
            const scannerHeight = Math.max(45, baseHeight);
            const scannerPos = getCenteredPosition(scannerWidth, scannerHeight);

            return (
                <Group
                    x={scannerPos.x}
                    y={scannerPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(scannerWidth, scannerHeight)}
                    <Rect
                        width={scannerWidth}
                        height={scannerHeight}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(2)}
                        strokeDasharray={[scaleUIElement(3), scaleUIElement(3)]}
                        cornerRadius={scaleUIElement(4)}
                    />
                    {/* Scanner viewfinder */}
                    <Rect
                        x={scannerWidth / 4}
                        y={scannerHeight / 4}
                        width={scannerWidth / 2}
                        height={scannerHeight / 2}
                        fill="transparent"
                        stroke="#10B981"
                        strokeWidth={scaleUIElement(2)}
                        cornerRadius={scaleUIElement(2)}
                    />
                    <Text
                        x={scannerWidth / 2}
                        y={scannerHeight / 2}
                        text="SCAN"
                        fontSize={6}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={scannerWidth}
                        align="center"
                        verticalAlign="middle"
                        offsetX={scannerWidth / 2}
                        offsetY={3}
                        fontStyle="bold"
                    />
                </Group>
            );

        case 'phoneKeypad':
            const phoneKeypadMeta = element.metadata as UIPhoneKeypadMetadata;
            const keypadWidth = scaleUIElement(phoneKeypadMeta.width || 300);
            const keypadHeight = scaleUIElement(phoneKeypadMeta.height || 400);
            const keypadPos = getCenteredPosition(keypadWidth, keypadHeight);

            return (
                <Group
                    x={keypadPos.x}
                    y={keypadPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(keypadWidth, keypadHeight)}
                    <Rect
                        width={keypadWidth}
                        height={keypadHeight}
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={scaleUIElement(4)}
                    />
                    {/* Display text area at top */}
                    <Rect
                        x={scaleUIElement(4)}
                        y={scaleUIElement(4)}
                        width={keypadWidth - scaleUIElement(8)}
                        height={scaleUIElement(20)}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={scaleUIElement(2)}
                    />
                    {/* Phone icon and text */}
                    <Text
                        x={keypadWidth / 2}
                        y={scaleUIElement(12)}
                        text="☎"
                        fontSize={scaleUIElement(8)}
                        fontFamily="Arial"
                        fill="#6B7280"
                        align="center"
                        offsetX={keypadWidth / 2}
                    />
                    {/* Keypad grid representation */}
                    {Array.from({ length: 4 }).map((_, row) =>
                        Array.from({ length: 3 }).map((_, col) => {
                            const buttonSize = Math.min(
                                keypadWidth / 4,
                                keypadHeight / 6,
                            );
                            const spacing = buttonSize * 0.2;
                            const startX =
                                (keypadWidth - buttonSize * 3 - spacing * 2) /
                                2;
                            const startY = scaleUIElement(30);
                            const x = startX + col * (buttonSize + spacing);
                            const y = startY + row * (buttonSize + spacing);

                            // Determine button shape
                            const isCircle =
                                phoneKeypadMeta.buttonShape === 'circle';
                            const cornerRadius =
                                phoneKeypadMeta.buttonShape ===
                                'roundedRectangle'
                                    ? scaleUIElement(4)
                                    : 0;

                            return isCircle ? (
                                <Circle
                                    key={`${row}-${col}`}
                                    x={x + buttonSize / 2}
                                    y={y + buttonSize / 2}
                                    radius={buttonSize / 2}
                                    fill={
                                        phoneKeypadMeta.buttonBackgroundColor ||
                                        '#FFFFFF'
                                    }
                                    stroke={
                                        phoneKeypadMeta.buttonBorderColor ||
                                        '#9CA3AF'
                                    }
                                    strokeWidth={scaleUIElement(0.5)}
                                />
                            ) : (
                                <Rect
                                    key={`${row}-${col}`}
                                    x={x}
                                    y={y}
                                    width={buttonSize}
                                    height={buttonSize}
                                    fill={
                                        phoneKeypadMeta.buttonBackgroundColor ||
                                        '#FFFFFF'
                                    }
                                    stroke={
                                        phoneKeypadMeta.buttonBorderColor ||
                                        '#9CA3AF'
                                    }
                                    strokeWidth={scaleUIElement(0.5)}
                                    cornerRadius={cornerRadius}
                                />
                            );
                        }),
                    )}
                    <Text
                        x={keypadWidth / 2}
                        y={keypadHeight - scaleUIElement(8)}
                        text="KEYPAD"
                        fontSize={scaleUIElement(4)}
                        fontFamily="Arial"
                        fill={phoneKeypadMeta.buttonTextColor || '#9CA3AF'}
                        align="center"
                        offsetX={keypadWidth / 2}
                    />
                </Group>
            );

        default:
            // Fallback for unknown element types
            const fallbackPos = getCenteredPosition(baseWidth, baseHeight);
            return (
                <Group
                    x={fallbackPos.x}
                    y={fallbackPos.y}
                    onClick={handleClick}
                    onContextMenu={konvaContextMenuHandler}
                    onMouseDown={handleMouseDown}
                >
                    {renderSelectionOutline(baseWidth, baseHeight)}
                    <Rect
                        width={baseWidth}
                        height={baseHeight}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={scaleUIElement(1)}
                        cornerRadius={scaleUIElement(4)}
                    />
                    <Text
                        x={baseWidth / 2}
                        y={baseHeight / 2}
                        text={String(element.type).toUpperCase()}
                        fontSize={6}
                        fontFamily="Arial"
                        fill="#6B7280"
                        width={baseWidth}
                        align="center"
                        verticalAlign="middle"
                    />
                </Group>
            );
    }
}
